import { IFieldsMapFactory } from "../../../fields/map"
import { showDialog } from "../../../react/dialog"
import { showWarning } from "../../../react/msgbox"
import { sum } from "../../../utils/array"
import { isImageContentType } from "../../../utils/utils"
import { DocumentPropertiesPopup } from "../../properties/PropsReact"
import { applyFilter, DoclibState, selectDocument, selectSize, selectYear } from "../../state"
import { allowedTypes, ImportDocumentVm } from "../../types"
import { getDocumentCssClass } from "../../utils"

/// <reference path="../../properties/PropsReact.tsx" />

interface Props {
    state: DoclibState,
    setState: (state: DoclibState) => void,
    mapFactory?: IFieldsMapFactory
}

export function DocumentLibrary({ state, setState, mapFactory }: Props) {

    const [files, setFiles] = React.useState<File[]>([])
    const filesExist = files && files.length
    const fileNames = filesExist ? files.map(f => f.name).join(', ') : '';

    const { svc,
        session,
        size,
        visibleDocs,
        search,
        years,
        year,
        documentSelection,
        multipleSelection
    } = state

    function handleFileChanged(e: React.ChangeEvent<HTMLInputElement>) {

        const fs = Array.from(e.target.files);

        if (fs && fs.length > 0) {

            setFiles(fs)

        }
    }

    function refresh() {
        svc.readFiles(year).then(res => {
            const newDocs = res.map(d => ({ document: d, selected: false }))
            const newVisibleDocs = filter(newDocs, search, state.limitToPictures)
            setState({ ...state, documents: newDocs, visibleDocs: newVisibleDocs })
        })
    }

    function filter(
        documents: ImportDocumentVm[],
        search: string,
        limitToPictures: boolean) {
        if (!search && !limitToPictures) { return documents }

        let docs = [...documents || []]

        if (limitToPictures) {
            docs = docs.filter(
                d => isImageContentType(
                    d.document.ContentType))
        }

        if (search) {

            search = search.toLowerCase()
            docs = docs.filter(d =>
                d.document.FileName.toLowerCase().indexOf(search) !== -1)
        }

        return docs
    }

    function showDetails(e: React.MouseEvent, doc: ImportDocumentVm) {

        e.stopPropagation()

        showDialog({
            content: DocumentPropertiesPopup,
            contentProps: {
                doc,
                svc,
                session,
                manageLibrary: state.manageLibrary,
                mapFactory,
            },
            size: "xlarge",
        }).then(res => {
            if (res) {
                refresh()
            }
        })
    }

    function importFile() {
        let filesCopy = [...files]

        const fileNames = filesCopy.map(f => f.name)
        const regex = new RegExp('(' + allowedTypes.join('|').replace(/\./g, '\\.') + ')$', "i")
        let valid = true

        fileNames.forEach(file => {
            if (!regex.test(file)) {
                showWarning("Allowed file types are : " + allowedTypes.join(", ") + ".", "Error")
                valid = false
                return
            }
        })

        if (!valid) { return }

        if (filesCopy.length > 10) {
            showWarning("Your selection is " + filesCopy.length
                    + " files long, the maximum number of files you can select is 10. Please unselect some files.",
                    "Error")
            return
        }

        const fileSize = Math.round(sum(filesCopy, f => (f.size / 1024 / 1024)) * 10) / 10

        if (fileSize > 100) {
            showWarning("The total files size is " + fileSize +
                "MB. The maximum total files size is 100MB.",
                "Error")
            return
        }

        const formData = new FormData()
        filesCopy.forEach(f => formData.append('files', f))

        svc.importFile(formData).then(res => {
            if (res.Error) {
                setFiles([])
                showWarning("There was an error during the import process. Be sure that the files are correct.", "Import error")
            } else {
                setFiles([])
                refresh()
            }
        })
    }

    function handleSelect(
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        d: ImportDocumentVm
    ) {

        if (!d) return

        if (documentSelection) {

            const copy = { ...state }

            if (!multipleSelection) [

                copy.documents.forEach(
                    d => d.selected = false)

            ]
            setState({
                ...copy,
                ...selectDocument(copy, d, !d.selected)
            }
            )
        } else {

            showDetails(e, d)

        }

    }

    return <div className="omnia-import-document-library">

        <div className="row material-form">

            <div className="col-md-4">
                <form className="omnia-form">
                    <div className="form-group file">
                        {state.manageLibrary &&
                            <div className="input-group input-source-file">
                                <span className="input-group-btn">
                                    <span className="btn btn-floating-action btn-success btn-floating-action-file">
                                        <i className="fas fa-plus"></i>
                                        <input type="file"
                                            accept=".png, .jpg, .jpeg, .gif, .pdf"
                                            multiple={true}
                                            onChange={handleFileChanged}
                                        />
                                    </span>
                                </span>
                                {
                                    fileNames && <input
                                        type="text"
                                        className="form-control file-name"
                                        value={fileNames}
                                        readOnly
                                    />
                                }
                            </div>
                        }
                    </div>
                </form>
            </div>

            <div className="col-md-2">
                {filesExist ?
                    <button className="btn btn-success btn-import" onClick={importFile}>
                        {files && files.length > 1 ? 'Import files' : 'Import file'}
                    </button>
                    : <></>
                }
            </div>

            <div className="col-md-6 filters">

                <span className="filters-label">Filters</span>

                <div className="form-group">
                    <select
                        className="form-control"
                        value={year}
                        onChange={e => selectYear(state, Number(e.target.value)).then(setState)}>
                        {years.map(y => <option key={y.Year} value={y.Year}>{y.Year}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <input
                        className="form-control"
                        type="text"
                        value={search || ""}
                        placeholder="Search..."
                        onChange={e => setState(applyFilter(state, e.target.value))} />
                </div>

                <span className="filters-label">Display</span>

                <div className="form-group">
                    <select
                        className="form-control"
                        value={size.size.id}
                        onChange={e => setState(selectSize(state, e.target.value))}>
                        {
                            size.sizes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                        }
                    </select>
                </div>

            </div>
        </div>

        <div className="document-container">

            {
                visibleDocs.map(d =>

                    <div key={d.document.ImportDocumentId}
                        className={getDocumentCssClass(size.size, d)}
                        onClick={e => handleSelect(e, d)}
                    >

                        <div>

                            {!state.manageLibrary &&
                                <div className="details">
                                    <button type="button" className="btn btn-default btn-floating-action small white" onClick={e => showDetails(e, d)}>
                                        <i className="fas fa-search"></i>
                                    </button>
                                </div>
                            }

                            <div className="image" onClick={e => handleSelect(e, d)}>
                                <img src={svc.getImgPath(d.document, "thumbnail")} />
                            </div>

                            <div className="caption">
                                {d.document.FileName}
                            </div>
                        </div>

                    </div>

                )
            }

        </div>
    </div>
}
